import { SelectionModel } from '@angular/cdk/collections';
import {
	Component,
	ContentChild,
	Directive,
	ElementRef,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { QueryParam } from 'src/app/services/api.services';
import { ApiService } from 'src/app/services/api.services';
import { MatButton } from '@angular/material/button';
import { NgClass } from '@angular/common';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatChipSet, MatChip } from '@angular/material/chips';

export type PresentationMode = 'table' | 'map';

export type Size = { width: number; height: number };

export type DropdownOption = { label: string; value: string };

export type PresentationFilters = {
	[name: string]: any;
};

export type DataSetFilter = {
	name: string;
	label: string;
	value: any;
	dropdownOptions?: DropdownOption[];
};

export type DataSetFilters = {
	[name: string]: {
		label: string;
		value: any;
		dropdownOptions?: DropdownOption[];
	};
};

@Component({
	selector: 'app-data-set-selected-chips',
	template: `
		@if (selected.length > 0) {
			<div class="data-set-selected-chips-container">
				<div class="data-set-selected-chips-scroll">
					<mat-chip-set class="data-set-selected-chips-set">
						@for (item of visibleChips; track trackByFn($index, item)) {
							<mat-chip class="filter-chip">
								{{ getDisplayText(item) }}
								<span
									class="removeIconCenterHack"
									style="margin-left: 6px; cursor: pointer;"
									(click)="removeItem(item)"
									(keydown.enter)="removeItem(item)"
									(keydown.space)="removeItem(item)"
									tabindex="0"
									role="button"
									[attr.aria-label]="getRemoveItemAriaLabel(item)"
									[attr.data-automation-id]="
										getDisplayText(item) + '-selection-remove-chip'
									"
								>
									<mat-icon class="filter-chip-remove-icon">close</mat-icon>
								</span>
							</mat-chip>
						}
						@if (hasOverflow) {
							<mat-chip class="filter-chip filter-chip-overflow">
								<span class="overflow-count">{{ overflowCount }}+</span>
							</mat-chip>
						}
					</mat-chip-set>
				</div>
				<button
					mat-button
					class="data-set-action-button-normal"
					(click)="clearAll()"
					i18n="@@dataSetSelectedChips-clearAll"
				>
					Clear All
				</button>
			</div>
		}
	`,
	styleUrls: ['./data-set.component.scss'],
	imports: [MatChipSet, MatChip, MatIcon, MatButton],
})
export class DataSetSelectedChipsComponent {
	@Input() selected: any[] = [];
	@Input() displayFormat?: (item: any) => string; // display format for selected items
	@Input() firstColumnProperty?: string; // display format for first column
	@Input() maxVisibleChips = 20;

	@Output() itemRemoved = new EventEmitter<any>();
	@Output() allCleared = new EventEmitter<void>();

	get visibleChips(): any[] {
		return this.selected.slice(0, this.maxVisibleChips);
	}

	get hasOverflow(): boolean {
		return this.selected.length > this.maxVisibleChips;
	}

	get overflowCount(): number {
		return Math.max(0, this.selected.length - this.maxVisibleChips);
	}

	getDisplayText(item: any): string {
		if (this.displayFormat) {
			return this.displayFormat(item);
		}

		// Use the first column property if provided
		if (this.firstColumnProperty) {
			const value = this.getNestedProperty(item, this.firstColumnProperty);
			if (value != null && value !== '') {
				return value.toString();
			}
		}

		// Fallback: use first non-null property value
		const keys = Object.keys(item);
		for (const key of keys) {
			const value = item[key];
			if (value != null && value !== '' && typeof value !== 'object') {
				return value.toString();
			}
		}

		return 'Unknown';
	}

	// Helper to get nested property values (e.g., "property.name" from item.property.name)
	private getNestedProperty(obj: any, path: string): any {
		return path.split('.').reduce((current, prop) => current?.[prop], obj);
	}

	removeItem(item: any): void {
		this.itemRemoved.emit(item);
	}

	clearAll(): void {
		this.allCleared.emit();
	}

	trackByFn(index: number, item: any): any {
		return index;
	}

	getRemoveItemAriaLabel(item: any): string {
		const removeText = $localize`:@@dataSetSelectedChips-removeItem:Remove`;
		return `${removeText} ${this.getDisplayText(item)}`;
	}
}

/**
 * This button class only exists bceause of the different styling of the buttons in the toolbar.
 */
@Component({
	selector: 'app-data-set-action-button',
	template: `
		<button
			mat-button
			[disabled]="this.isDisabled"
			[ngClass]="{
				'data-set-action-button-normal': !selected,
				'data-set-action-button-active': selected,
			}"
			data-automation-id="data-set-action-button"
		>
			@if (showIcon) {
				<mat-icon
					[svgIcon]="svgIcon"
					style="margin-right: 8px"
					class="mat-icon"
					>{{ icon }}</mat-icon
				>
			}
			<span>{{ label }}</span>
		</button>
	`,
	styleUrls: ['./data-set.component.scss', '../../ui/common/main.scss'],
	imports: [MatButton, NgClass, MatIcon],
})
export class DataSetActionButtonComponent {
	constructor() {}

	@Input() icon = '';
	@Input() svgIcon = '';
	@Input() label = '';
	@Input() selected = false;
	@Input() isDisabled = false;

	get showIcon(): boolean {
		return this.svgIcon !== '' || this.icon !== '';
	}
}

@Component({
	selector: 'app-data-set-presentation-toggle',
	template: `
		<div style="display: flex">
			<button
				mat-button
				class="data-set-action-group"
				[ngClass]="{
					'data-set-action-group-toggle-active': tableMode,
					'data-set-action-group-toggle-inactive': !tableMode,
				}"
				(click)="changePresentationMode('table')"
				data-automation-id="data-set-show-table-button"
			>
				<mat-icon svgIcon="sl-hamburger-menu"></mat-icon>
			</button>
			<button
				mat-button
				class="data-set-action-group"
				[ngClass]="{
					'data-set-action-group-toggle-active': mapMode,
					'data-set-action-group-toggle-inactive': !mapMode,
				}"
				(click)="changePresentationMode('map')"
				data-automation-id="data-set-show-map-button"
			>
				<mat-icon svgIcon="sl-map-pin"></mat-icon>
			</button>
		</div>
	`,
	imports: [MatButton, NgClass, MatIcon],
})
export class DataSetPresentationToggleComponent {
	private currentMode: PresentationMode = 'table';

	constructor(private dataSet: DataSetComponent) {
		dataSet.presentationMode.subscribe((mode) => {
			this.currentMode = mode;
		});
	}

	changePresentationMode(mode: PresentationMode): void {
		this.dataSet.changePresentationMode(mode);
	}

	get tableMode(): boolean {
		return this.currentMode === 'table';
	}

	get mapMode(): boolean {
		return this.currentMode === 'map';
	}
}

@Component({
	selector: 'app-data-set-selected-count',
	template: `
		<div class="data-set-action-group-selected-count mat-caption">
			<span class="data-set-action-group-selected-count-total">{{ count }}</span
			>&nbsp;Selected
		</div>
	`,
})
export class DataSetSelectedCountComponent implements OnDestroy {
	private selectedSubscription?: Subscription;
	count = 0;

	constructor(private dataSet: DataSetComponent) {
		this.updateCount();
		this.selectedSubscription = this.dataSet.selectedChanged.subscribe(() => {
			this.updateCount();
		});
	}

	private updateCount(): void {
		this.count = this.dataSet.selected.length;
	}

	ngOnDestroy(): void {
		this.selectedSubscription?.unsubscribe();
		this.selectedSubscription = undefined;
	}
}

// Shows bulk action buttons and selected count ONLY when table rows are selected
// and applies styles to the action buttons and selected count container
@Directive({ selector: '[appSelectedActionGroup]' })
export class DataSetSelectedActionGroupDirective implements OnDestroy {
	private hasView = false;

	private selectionSubscription?: Subscription;

	constructor(
		private templateRef: TemplateRef<any>,
		private viewContainer: ViewContainerRef,
		private dataSet: DataSetComponent
	) {
		this.selectionSubscription = this.dataSet.selectedChanged.subscribe(() => {
			this.updateView();
		});

		this.updateView();
	}

	ngOnDestroy(): void {
		this.selectionSubscription?.unsubscribe();
		this.selectionSubscription = undefined;
	}

	private updateView(): void {
		const selected = this.dataSet.selected.length > 0;

		if (selected && !this.hasView) {
			const ref = this.viewContainer.createEmbeddedView(this.templateRef);
			const rootElement = ref.rootNodes[0];

			// Check if this is the selected count container vs selected action buttons
			if (rootElement.classList.contains('data-set-selected-count-container')) {
				rootElement.className = 'data-set-selected-count-container';
			} else {
				rootElement.className =
					'data-set-action-group data-set-selected-action-group';
			}

			this.hasView = true;
		} else if (!selected && this.hasView) {
			this.viewContainer.clear();
			this.hasView = false;
		}
	}
}

// Needed when component has action buttons and those buttons should hide when items are selected
@Directive({ selector: '[appActionGroup]' })
export class DataSetPrimaryActionGroupDirective implements OnDestroy {
	private hasView = false;

	private selectionSubscription?: Subscription;
	private selectedChangedSubscription?: Subscription;
	private paginationSubscription?: Subscription;

	constructor(
		private templateRef: TemplateRef<any>,
		private viewContainer: ViewContainerRef,
		private dataSet: DataSetComponent
	) {
		this.selectionSubscription = this.dataSet.selection.changed.subscribe(
			(_) => {
				this.updateView();
			}
		);

		// Also listen to selectedChanged to catch setting
		this.selectedChangedSubscription = this.dataSet.selectedChanged.subscribe(
			() => {
				this.updateView();
			}
		);

		// Listen to pagination changes to update when switching pages
		this.paginationSubscription =
			this.dataSet.presentationFilterChanged.subscribe(() => {
				this.updateView();
			});

		this.updateView();
	}

	ngOnDestroy(): void {
		this.selectionSubscription?.unsubscribe();
		this.selectionSubscription = undefined;
		this.selectedChangedSubscription?.unsubscribe();
		this.selectedChangedSubscription = undefined;
		this.paginationSubscription?.unsubscribe();
		this.paginationSubscription = undefined;
	}

	private updateView(): void {
		// Action buttons should ALWAYS hide when there are persistent selections
		const hasPersistentSelections = this.dataSet.persistentSelectionCount > 0;

		if (!hasPersistentSelections && !this.hasView) {
			const ref = this.viewContainer.createEmbeddedView(this.templateRef);
			ref.rootNodes[0].className = 'data-set-action-group';
			this.hasView = true;
		} else if (hasPersistentSelections && this.hasView) {
			this.viewContainer.clear();
			this.hasView = false;
		}
	}
}

// Shows search bar and filters ONLY when table rows are selected
// and applies styles to the search bar and filters container
@Directive({ selector: '[appSearchActionGroup]' })
export class DataSetSearchActionGroupDirective implements OnDestroy {
	private hasView = false;

	private selectionSubscription?: Subscription;
	private selectedChangedSubscription?: Subscription;
	private paginationSubscription?: Subscription;

	constructor(
		private templateRef: TemplateRef<any>,
		private viewContainer: ViewContainerRef,
		private dataSet: DataSetComponent
	) {
		this.selectionSubscription = this.dataSet.selection.changed.subscribe(
			(_) => {
				this.updateView();
			}
		);

		// Also listen to selectedChanged to catch setting changes
		this.selectedChangedSubscription = this.dataSet.selectedChanged.subscribe(
			() => {
				this.updateView();
			}
		);

		// Listen to pagination changes to update when switching pages
		this.paginationSubscription =
			this.dataSet.presentationFilterChanged.subscribe(() => {
				this.updateView();
			});

		this.updateView();
	}

	ngOnDestroy(): void {
		this.selectionSubscription?.unsubscribe();
		this.selectionSubscription = undefined;
		this.selectedChangedSubscription?.unsubscribe();
		this.selectedChangedSubscription = undefined;
		this.paginationSubscription?.unsubscribe();
		this.paginationSubscription = undefined;
	}

	private updateView(): void {
		const hasPersistentSelections = this.dataSet.persistentSelectionCount > 0;

		if (this.dataSet.isPersistentSelectionEnabled) {
			// Should always show search bar and filters, even when items are selected
			if (!this.hasView) {
				const ref = this.viewContainer.createEmbeddedView(this.templateRef);
				ref.rootNodes[0].className = 'data-set-action-group';
				this.hasView = true;
			}
		} else {
			// Should hide search bar when there are persistent selections
			if (!hasPersistentSelections && !this.hasView) {
				const ref = this.viewContainer.createEmbeddedView(this.templateRef);
				ref.rootNodes[0].className = 'data-set-action-group';
				this.hasView = true;
			} else if (hasPersistentSelections && this.hasView) {
				this.viewContainer.clear();
				this.hasView = false;
			}
		}
	}
}

@Component({
	selector: 'app-data-set-toolbar',
	template: ` <div
			*appSelectedActionGroup
			class="data-set-selected-count-container"
		>
			<app-data-set-selected-count></app-data-set-selected-count>
		</div>
		<ng-content></ng-content>`,
	styleUrls: ['./data-set.component.scss'],
	imports: [DataSetSelectedActionGroupDirective, DataSetSelectedCountComponent],
})
export class DataSetToolbarComponent {
	constructor() {}
}

@Component({
	selector: 'app-data-set-content',
	template: '<div #content><ng-content></ng-content></div>',
	styleUrls: ['./data-set.component.scss'],
})
export class DataSetContentComponent {
	@ViewChild('content', { read: ElementRef }) content!: ElementRef;

	constructor() {}

	get dataSetContentDimensions(): Size {
		const element = this.content.nativeElement;
		return {
			width: element.offsetWidth,
			height: element.offsetHeight,
		};
	}
}

@Component({
	selector: 'app-data-set',
	template: ` <div class="data-set-container">
		<ng-content select="app-data-set-toolbar"></ng-content>
		@if (isPersistentSelectionEnabled) {
			<app-data-set-selected-chips
				[selected]="selected"
				[displayFormat]="selectedDisplayFormat"
				[firstColumnProperty]="firstColumnProperty"
				(itemRemoved)="onSelectedItemRemoved($event)"
				(allCleared)="onSelectedAllCleared()"
			>
			</app-data-set-selected-chips>
		}
		<ng-content select="app-data-set-content"></ng-content>
		<ng-content select="app-data-set-map-view"></ng-content>
	</div>`,
	styleUrls: ['./data-set.component.scss'],
	imports: [DataSetSelectedChipsComponent],
})
export class DataSetComponent implements OnInit, OnDestroy {
	@HostBinding('class') hostClass = 'app-data-set';

	private _dataSource!: MatTableDataSource<any>;
	private dataSubscription?: Subscription;
	@Input() rowId!: (row: any) => string; // REQUIRED: (at least for item selection) must be provided by parent
	@Input()
	set dataSource(ds: MatTableDataSource<any>) {
		this._dataSource = ds;
		this.dataSubscription?.unsubscribe();
		this.dataSubscription = this._dataSource.connect().subscribe(() => {
			this.syncSelectionWithTable();
		});
		this.syncSelectionWithTable();
	}
	get dataSource(): MatTableDataSource<any> {
		return this._dataSource;
	}
	@Input() defaultPresentationMode: PresentationMode = 'table';
	@Input() selectedDisplayFormat?: (item: any) => string;
	@Input() firstColumnProperty?: string;
	@Input() enablePersistentSelection: boolean = false; // Enables persistent selection to be shown
	@ContentChild(DataSetContentComponent)
	dataSetContent!: DataSetContentComponent;
	@Output() dataSetFilterChanged = new EventEmitter<DataSetFilter[]>();
	@Output() presentationFilterChanged = new EventEmitter<PresentationFilters>();
	@Output() presentationMode = new EventEmitter<PresentationMode>();
	@Output() selectedChanged = new EventEmitter<void>();

	selection = new SelectionModel<any>(true, []); // SelectionModel is used to track the selection of the table rows
	private _selected: any[] = []; // Store actual selected objects
	private queryParamsSubject: BehaviorSubject<QueryParam[]> =
		new BehaviorSubject<QueryParam[]>([]);

	// This is the persistent selection
	get selected(): any[] {
		// Always return persistent selections to support cross-page operations
		return this._selected;
	}

	// Get persistent selection count
	get persistentSelectionCount(): number {
		return this._selected.length;
	}

	// Getter for enabling persistent selection
	get isPersistentSelectionEnabled(): boolean {
		return this.enablePersistentSelection;
	}

	constructor(private apiService: ApiService) {
		// Patch the SelectionModel so .selected always returns the persistent selection
		Object.defineProperty(this.selection, 'selected', {
			get: () => this.selected,
			configurable: true,
			enumerable: true,
		});

		// Patch select to only allow current page selection
		const originalSelect = this.selection.select.bind(this.selection);
		this.selection.select = (...items: any[]) => {
			const currentPageData = this.getCurrentPageData();
			const currentPageIds = new Set(
				currentPageData.map((row) => this.rowId(row))
			);
			const filteredItems = items.filter((item) =>
				currentPageIds.has(this.rowId(item))
			);
			return originalSelect(...filteredItems);
		};
	}

	// Method to explicitly clear both current page and persistent selections
	clearAllSelections(): void {
		this.selection.clear();
		this._selected = [];
		this.selectedChanged.emit();
	}

	get queryParams(): Observable<QueryParam[]> {
		return this.queryParamsSubject.asObservable().pipe(debounceTime(100));
	}

	private draftFilters: DataSetFilters = {};
	private appliedFilters: DataSetFilters = {};
	private presentationFilters: PresentationFilters = {};
	private filterSubscription?: Subscription;
	private selectionSubscription?: Subscription;
	private isFiltering = false; // Flag to track when filtering is in progress

	ngOnInit(): void {
		this.presentationMode.emit(this.defaultPresentationMode);

		// Listen to selection changes and persist objects
		this.selectionSubscription = this.selection.changed.subscribe((change) => {
			// Skip if we're currently filtering/syncing to prevent clearing persistent selection
			if (this.isFiltering) return;

			// Add newly selected objects
			change.added.forEach((item) => {
				const itemId = this.rowId(item);
				if (
					!this._selected.some(
						(selectedRow) => this.rowId(selectedRow) === itemId
					)
				) {
					this._selected.push(item);
					this.selectedChanged.emit();
				}
			});
			// Remove deselected objects
			change.removed.forEach((item) => {
				const itemId = this.rowId(item);
				this._selected = this._selected.filter(
					(row) => this.rowId(row) !== itemId
				);
				this.selectedChanged.emit();
			});
		});

		this.filterSubscription = combineLatest([
			this.dataSetFilterChanged.pipe(startWith([])),
			this.presentationFilterChanged.pipe(startWith({})),
		])
			.pipe(
				map((merged) => {
					const data = merged[0];
					const presentation = merged[1];

					const params: QueryParam[] = data
						.map((it) => ({ param: it.name, value: it.value }))
						.concat(
							Object.entries(presentation).map(([name, value]) => ({
								param: name,
								value,
							}))
						);
					return params;
				})
			)
			.subscribe((params) => {
				this.queryParamsSubject.next(params);
			});
	}

	onSelectedItemRemoved(item: any): void {
		// Remove from both selection and persistent objects
		this.selection.deselect(item);
		if (this.isPersistentSelectionEnabled) {
			const itemId = this.rowId(item);
			this._selected = this._selected.filter(
				(row) => this.rowId(row) !== itemId
			);
			// Sync selection model with updated persistent selection
			this.syncSelectionWithTable();
		}
		this.selectedChanged.emit();
	}

	onSelectedAllCleared(): void {
		this.selection.clear();
		if (this.isPersistentSelectionEnabled) {
			this._selected = [];
			// Sync selection model with cleared persistent selection
			this.syncSelectionWithTable();
		}
		this.selectedChanged.emit();
	}

	// Call this method after updating dataSource.data to sync checkboxes with selectedObjects
	syncSelectionWithTable(): void {
		if (!this.dataSource?.data || this.dataSource.data.length === 0) return;

		this.isFiltering = true;

		// Clear the current selection first
		this.selection.clear();

		// Then select only the rows that are currently in the table and have persistent IDs
		this.dataSource.data.forEach((row) => {
			if (
				this._selected.some(
					(selectedRow) => this.rowId(selectedRow) === this.rowId(row)
				)
			) {
				this.selection.select(row);
			}
		});

		this.isFiltering = false;
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
		this.selectionSubscription?.unsubscribe();
		this.selectionSubscription = undefined;
		this.dataSubscription?.unsubscribe();
		this.dataSubscription = undefined;
	}

	changePresentationMode(mode: PresentationMode): void {
		this.presentationMode.emit(mode);
	}

	filter(
		name: string,
		label?: string,
		value?: { label: string; value: any },
		dropdownOptions?: { label: string; value: any }[]
	): { label: string; value: any } | void {
		if (label === undefined) {
			return this.draftFilters[name]?.value;
		} else {
			const filterValue = this.getFilterApiValue(value);
			if (
				filterValue !== undefined &&
				filterValue !== null &&
				filterValue !== ''
			) {
				this.draftFilters[name] = {
					label,
					value,
					dropdownOptions,
				};
			} else {
				delete this.draftFilters[name];
			}
		}
	}

	prepareFiltersForEditing(): void {
		this.draftFilters = {};
		Object.assign(this.draftFilters, this.appliedFilters);
	}

	resetEditedFilters(): void {
		this.draftFilters = {};
		Object.assign(this.draftFilters, this.appliedFilters);
	}

	applyFilters(): void {
		this.appliedFilters = {};
		Object.assign(this.appliedFilters, this.draftFilters);
		this.notifyOfFilterChanges();
	}

	removeFilter(name: string): void {
		delete this.appliedFilters[name];
		this.notifyOfFilterChanges();
	}

	updatePresentationFilters(presentationFilters: PresentationFilters): void {
		this.presentationFilters = presentationFilters;
		this.presentationFilterChanged.emit(this.presentationFilters);
	}

	notifyOfFilterChanges(): void {
		const mapped = Object.entries(this.appliedFilters).map(([key, value]) => ({
			name: key,
			label: value.label,
			value: this.getFilterApiValue(value.value),
			dropdownOptions: value.dropdownOptions,
		}));
		this.dataSetFilterChanged.emit(mapped);
	}

	getCurrentAppliedFilters(): DataSetFilter[] {
		return Object.entries(this.appliedFilters).map(([key, value]) => ({
			name: key,
			label: value.label,
			value: this.getFilterApiValue(value.value),
			dropdownOptions: value.dropdownOptions,
		}));
	}

	private getFilterApiValue(value: any): any {
		if (
			value !== null &&
			typeof value === 'object' &&
			Object.prototype.hasOwnProperty.call(value, 'value')
		) {
			return value.value;
		}
		return value;
	}

	clearFilters(): void {
		this.appliedFilters = {};
		this.draftFilters = {};
		this.notifyOfFilterChanges();
	}

	get anyActiveFilters(): boolean {
		return Object.keys(this.appliedFilters).length > 0;
	}

	get dataSetContentDimensions(): {
		top: number;
		right: number;
		bottom: number;
		left: number;
	} {
		return this.dataSetContent.content.nativeElement.getBoundingClientRect();
	}

	// Helper to get current page data
	public getCurrentPageData(): any[] {
		if (this.dataSource && this.dataSource.paginator) {
			const startIndex =
				this.dataSource.paginator.pageIndex *
				this.dataSource.paginator.pageSize;
			return this.dataSource.data.slice(
				startIndex,
				startIndex + this.dataSource.paginator.pageSize
			);
		}
		return this.dataSource?.data || [];
	}
}
